import * as React from "react";

import { Slot } from "radix-ui";

import { cn } from "@/utils/cn";

import Text from "@/components/text";

interface ListProps extends React.ComponentProps<"div"> {
  withBg?: boolean;
}

function List({ className, withBg = true, ...props }: ListProps) {
  return (
    <div
      data-slot="list"
      className={cn(
        "flex w-full flex-col overflow-hidden rounded-xl",
        withBg ? "bg-tg-section-bg" : "bg-transparent",
        className,
      )}
      {...props}
    />
  );
}

function ListHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="list-header"
      className={cn(
        "flex w-full items-center justify-between ps-(--list-inset) pe-(--list-inset) pt-2.5 pb-1.5",
        className,
      )}
      {...props}
    />
  );
}

function ListTitle({ className, ...props }: React.ComponentProps<typeof Text.Accent>) {
  return <Text.Accent weight="medium" className={className} {...props} />;
}

const listItemBase = "flex w-full items-center gap-3 px-(--list-inset) py-2.5 text-start transition-colors";

interface ListItemProps extends React.ComponentProps<"div"> {
  asChild?: boolean;
  interactive?: boolean;
}

function ListItem({ className, asChild, interactive, ...props }: ListItemProps) {
  const Comp = asChild ? Slot.Root : "div";

  return (
    <Comp
      data-slot="list-item"
      className={cn(
        listItemBase,
        interactive && "cursor-pointer hover:bg-tg-secondary-bg/50 active:bg-tg-secondary-bg/50",
        className,
      )}
      {...props}
    />
  );
}

function ListItemIcon({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div data-slot="list-item-icon" className={cn("flex shrink-0 items-center justify-center", className)} {...props} />
  );
}

interface ListItemContentProps extends React.ComponentProps<"div"> {
  asChild?: boolean;
}

function ListItemContent({ className, asChild, ...props }: ListItemContentProps) {
  const Comp = asChild ? Slot.Root : "div";

  return <Comp data-slot="list-item-content" className={cn("flex min-w-0 flex-1 flex-col", className)} {...props} />;
}

function ListItemTrailing({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div data-slot="list-item-trailing" className={cn("flex shrink-0 items-center gap-1", className)} {...props} />
  );
}

export { List, ListHeader, ListItem, ListItemContent, ListItemIcon, ListItemTrailing, ListTitle };
